import React, { useEffect, useState } from 'react';
import CollectionPreferences, {
	CollectionPreferencesProps,
} from '@amzn/awsui-components-console/collection-preferences';
import Pagination from '@amzn/awsui-components-console/pagination';
import Popover from '@amzn/awsui-components-console/popover';
import TextFilter from '@amzn/awsui-components-console/text-filter';
import Modal from '@amzn/awsui-components-console/modal';

import { FlashbarProps } from '@amzn/awsui-components-console/flashbar';
import Table from '@amzn/awsui-components-console/table';
import Button from '@amzn/awsui-components-console/button';
import Header from '@amzn/awsui-components-console/header';
import { useCollection } from '@amzn/awsui-collection-hooks';
import EmptyState from 'src/components/commonComponents/table/emptyTableWithActions';
import ShowSnowStatus from 'src/components/findEntity/ShowSNOWStatus';
import {
	getDateAndTimeFromEpoch,
	sortedTaskStatus,
} from 'src/utils/CommonUtils';
import { TaskExecutionData } from '@amzn/snow-nxt-workflow-service-typescript-client';
import TaskActionButtons from '../selfServeWorkflows/commonComponents/taskActionButtons';
import SubTaskExecutionView from 'src/components/selfServeWorkflows/SubTaskExecution/SubTaskExecutionView';
import { UserIcon } from '../commonComponents/UserIcon';
import { useTaskOutput } from 'src/hooks/useTaskOutput';
import { logger } from 'src/utils/logger';

export interface WorkflowExecutionTableProps {
	workflowId: string;
	workflowExecutionId: string;
	taskExecutionData: TaskExecutionData[];
	entityName: string;
	show?: boolean;
	refreshView?: () => void;
	setFlashBarItems?: (items: FlashbarProps.MessageDefinition[]) => void;
	hasExecutePermissions: boolean;
	statusFilter?: string | null;
	tableTitle?: string;
}
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';

const TASK_EXECUTION_PREFERENCES_KEY = 'workflowExecutionTable-preferences-v1';

const CONTENT_DISPLAY_OPTIONS = [
	{ id: 'TaskStatus', label: 'Task Status' },
	{ id: 'TaskName', label: 'Task Name', alwaysVisible: true },
	{ id: 'TaskType', label: 'Task Type' },
	{ id: 'LastStatusUpdatedBy', label: 'Last Status Updated By' },
	{ id: 'Rank', label: 'Rank' },
	{ id: 'HopRank', label: 'Hop Rank' },
	{ id: 'ActualStartTime', label: 'Actual Start Time' },
	{ id: 'NoOfAttempts', label: 'Number Of Attempts' },
	{ id: 'UserComment', label: 'Comment by User' },
	{ id: 'TaskId', label: 'Task ID' },
	{ id: 'Entities', label: 'Entities' },
	{ id: 'TaskDefinitionVersion', label: 'Task Definition Version' },
	{ id: 'TaskRunId', label: 'Task Run ID' },
	{ id: 'ExecutionId', label: 'Execution ID' },
	{ id: 'LastUpdatedBy', label: 'Last Updated By' },
	{ id: 'LatestAttempt', label: 'Latest Attempt' },
	{ id: 'LastStatusUpdateTime', label: 'Last Status Update Time' },
	{ id: 'ActualCompletionTime', label: 'Actual Completion Time' },
	{ id: 'CreationTime', label: 'Creation Time' },
];

const DEFAULT_VISIBLE = [
	'TaskStatus',
	'TaskName',
	'TaskType',
	'LastStatusUpdatedBy',
	'UserComment',
];

export default function WorkflowExecutionTable({
	taskExecutionData,
	show = true,
	workflowId,
	workflowExecutionId,
	refreshView,
	setFlashBarItems,
	hasExecutePermissions,
	statusFilter,
	tableTitle = 'Workflow Execution Tasks',
}: WorkflowExecutionTableProps) {
	const [preferences, setPreferences] = useState<
		Partial<CollectionPreferencesProps.Preferences>
	>(() => {
		try {
			const saved = localStorage.getItem(TASK_EXECUTION_PREFERENCES_KEY);

			if (saved) {
				return JSON.parse(saved);
			}
		} catch (error) {
			logger.error('Error loading table preferences', error as Error);
		}

		return {
			pageSize: 10,
			contentDisplay: CONTENT_DISPLAY_OPTIONS.map(({ id }) => ({
				id,
				visible: DEFAULT_VISIBLE.includes(id),
			})),
			stripedRows: getDefaultStripedRows(),
			wrapLines: true,
		};
	});

	const handlePreferencesChange = (
		detail: Partial<CollectionPreferencesProps.Preferences>
	) => {
		setPreferences(detail);
		try {
			localStorage.setItem(
				TASK_EXECUTION_PREFERENCES_KEY,
				JSON.stringify(detail)
			);
		} catch (error) {
			logger.error('Error saving table preferences', error as Error);
		}
	};

	const [selectedTask, setSelectedTask] = useState<Partial<TaskExecutionData>>(
		{}
	);
	const [selectedItems, setSelectedItems] = useState<TaskExecutionData[]>([]);

	const [visible, setVisible] = useState(false);

	const {
		taskOutput,
		isLoadingTaskOutput,
		fetchTaskOutputData,
		resetTaskOutput,
	} = useTaskOutput({ workflowId, workflowExecutionId });

	const getSnowTaskStatus = (status: string) => {
		return <ShowSnowStatus status={status} />;
	};

	const columnDefinitions = [
		{
			id: 'TaskStatus',
			header: 'Task Status',
			cell: (item) => <div>{getSnowTaskStatus(item.status)}</div>,
			sortingField: 'TaskStatus',
			sortingComparator: (a, b) => {
				return sortedTaskStatus(a.status, b.status);
			},
		},
		{
			id: 'TaskName',
			header: 'Task Name',
			cell: (item) => (
				<Button
					onClick={(e) => {
						e.preventDefault();
						setSelectedItems([item]);
						setSelectedTask(item);
						setVisible(true);
						fetchTaskOutputData(item);
					}}
					variant="inline-link"
				>
					{item.taskName}
				</Button>
			),
			minWidth: 270,
		},
		{
			id: 'TaskId',
			header: 'Task Id',
			cell: (item) => item.taskId,
		},
		{
			id: 'Entities',
			header: 'Entities',
			cell: (item) => item.entities,
		},
		{
			id: 'TaskDefinitionVersion',
			header: 'Task Definition Version',
			cell: (item) => item.taskDefinitionVersion,
		},
		{
			id: 'TaskType',
			header: 'Task Type',
			cell: (item) => item.taskType,
			minWidth: 260,
		},
		{
			id: 'Rank',
			header: 'Rank',
			cell: (item) => item.rank,
			sortingField: 'Rank',
			sortingComparator: (a, b) => {
				return a.rank - b.rank;
			},
		},
		{
			id: 'HopRank',
			header: 'Hop Rank',
			cell: (item) => item.hopRank,
			sortingField: 'HopRank',
			sortingComparator: (a, b) => {
				return a.hopRank - b.hopRank;
			},
		},
		{
			id: 'ActualStartTime',
			header: 'Actual Start Time',
			cell: (item) =>
				item.startTime ? getDateAndTimeFromEpoch(item.startTime) : '-',
		},
		{
			id: 'LastStatusUpdatedBy',
			header: 'Last Status Updated By',
			cell: (item) => <UserIcon user={item.lastUpdatedBy} size="small" />,
		},
		{
			id: 'ExecutionId',
			header: 'Execution Id',
			cell: (item) => item.taskExecutionId,
		},
		{
			id: 'TaskRunId',
			header: 'Task Run Id',
			cell: (item) => item.taskRunId,
		},
		{
			id: 'LastStatusUpdateTime',
			header: 'Last Status Update Time',
			cell: (item) =>
				item.lastUpdateTime
					? getDateAndTimeFromEpoch(item.lastUpdateTime)
					: '-',
		},
		{
			id: 'UserComment',
			header: 'User Comment',
			cell: (item) => item.comment,
			minWidth: 240,
		},
		{
			id: 'LatestAttempt',
			header: 'Latest Attempt',
			cell: (item) => item.latestAttempt,
			minWidth: 240,
		},
		{
			id: 'LastUpdatedBy',
			header: 'Last Updated By',
			cell: (item) => <UserIcon user={item.lastUpdatedBy} size="small" />,
		},
		{
			id: 'NoOfAttempts',
			header: 'Retry Count',
			cell: (item) => (
				<div>
					{Number.parseInt(item.attempt) > 0 && item.comment !== null ? (
						<Popover
							header={item.comment}
							content={
								<h2>
									<UserIcon user={item.lastUpdatedBy} size="small" />
								</h2>
							}
						>
							{item.attempt}
						</Popover>
					) : (
						'0'
					)}
				</div>
			),
			sortingField: 'NoOfAttempts',
		},
		{
			id: 'ActualCompletionTime',
			header: 'Actual Completion Time',
			cell: (item) =>
				item.completionTime
					? getDateAndTimeFromEpoch(item.completionTime)
					: '-',
		},
	];

	const {
		items,
		actions,
		filteredItemsCount,
		collectionProps,
		filterProps,
		paginationProps,
	} = useCollection(taskExecutionData, {
		filtering: {
			empty: (
				<EmptyState title="No instances" subtitle="No instances to display." />
			),
			noMatch: (
				<EmptyState
					title="No matches"
					subtitle="We can’t find a match."
					action={
						<Button onClick={() => actions.setFiltering('')}>
							Clear filter
						</Button>
					}
				/>
			),
			filteringFunction: (item, filteringText) => {
				if (!filteringText) {
					return true;
				}

				const statusValues = [
					'COMPLETED',
					'RUNNING',
					'WAITING',
					'FAILED',
					'OVERRIDEN',
					'READY',
					'IN_PROGRESS',
					'SCHEDULED',
					'RE_RUN',
					'PENDING_APPROVAL',
					'PENDING_REVIEW',
					'SCHEDULED_FOR_DEPLOYMENT',
					'SKIPPED',
					'TOTAL',
				];

				if (statusValues.includes(filteringText.toUpperCase())) {
					if (filteringText.toUpperCase() === 'RUNNING') {
						return [
							'RUNNING',
							'IN_PROGRESS',
							'PENDING_APPROVAL',
							'PENDING_REVIEW',
							'SCHEDULED_FOR_DEPLOYMENT',
						].includes(item.status?.toUpperCase() || '');
					}

					return item.status?.toUpperCase() === filteringText.toUpperCase();
				}

				const searchText = filteringText.toLowerCase();

				return Object.values(item).some((value) =>
					String(value).toLowerCase().includes(searchText)
				);
			},
		},
		pagination: { pageSize: preferences.pageSize },
		sorting: {
			defaultState: {
				sortingColumn: columnDefinitions[0],
				isDescending: true,
			},
		},
	});

	useEffect(() => {
		if (actions && actions.setFiltering) {
			if (statusFilter && statusFilter !== 'TOTAL') {
				actions.setFiltering(statusFilter);
			} else {
				actions.setFiltering('');
			}
		}
	}, [statusFilter, actions]);

	const COLLECTION_PREFERENCES = {
		title: 'Preferences',
		confirmLabel: 'Confirm',
		cancelLabel: 'Cancel',
		pageSizePreference: {
			title: 'Page size',
			options: [
				{ value: 10, label: '10 tasks' },
				{ value: 25, label: '25 tasks' },
				{ value: 50, label: '50 tasks' },
			],
		},
		contentDisplayPreference: {
			title: 'Column preferences',
			options: CONTENT_DISPLAY_OPTIONS,
		},
		stripedRowsPreference: {
			label: 'Striped rows',
			description: 'Select to add alternating shaded rows',
		},
		wrapLinesPreference: {
			label: 'Wrap lines',
			description: 'Select to wrap long text content in table cells',
		},
	};

	return show ? (
		<>
			{selectedTask && (
				<Modal
					size="large"
					onDismiss={() => {
						setVisible(false);
						resetTaskOutput();
					}}
					visible={visible}
				header={selectedTask.taskName}
			>
				{visible && (
					<SubTaskExecutionView
						taskDetails={selectedTask}
						workflowId={workflowId}
						workflowExecutionId={workflowExecutionId}
						taskOutput={taskOutput}
						isLoadingTaskOutput={isLoadingTaskOutput}
					/>
				)}
			</Modal>
			)}
			<Table
				{...collectionProps}
				resizableColumns
				stickyHeader
				loadingText="Loading resources"
				columnDefinitions={columnDefinitions}
				items={items}
				wrapLines={preferences.wrapLines}
				stripedRows={preferences.stripedRows}
				selectionType="single"
				selectedItems={selectedItems}
				onSelectionChange={({ detail }) => {
					setSelectedItems(detail.selectedItems);

					setSelectedTask(detail.selectedItems[0]);
				}}
				onRowClick={({ detail }) => {
					setSelectedItems([detail.item]);

					setSelectedTask(detail.item);
				}}
				header={
					<Header
						variant="h2"
						counter={`(${filteredItemsCount})`}
						actions={
							<TaskActionButtons
								disabled={!selectedTask?.taskId}
								refreshView={refreshView}
								taskDetails={selectedTask}
								workflowId={workflowId}
								workflowExecutionId={workflowExecutionId}
								hasExecutePermissions={hasExecutePermissions}
								setFlashBarItems={setFlashBarItems}
								showRefreshButton={true}
							/>
						}
					>
						{tableTitle}
					</Header>
				}
				columnDisplay={preferences.contentDisplay}
				variant="container"
				pagination={<Pagination {...paginationProps} />}
				preferences={
					<CollectionPreferences
						{...COLLECTION_PREFERENCES}
						preferences={preferences}
						onConfirm={({ detail }) => handlePreferencesChange(detail)}
					/>
				}
				filter={<TextFilter {...filterProps} />}
			></Table>
		</>
	) : null;
}
